import type { IconType } from "react-icons";
import {
  TbAlertCircle,
  TbAlertTriangle,
  TbArrowNarrowRight,
  TbBuildingBank,
  TbCashBanknote,
  TbCheck,
  TbCircleCheck,
  TbCircleDashed,
  TbCircleX,
  TbCoin,
  TbCreditCard,
  TbFile,
  TbFileCertificate,
  TbFileDescription,
  TbFlagCheck,
  TbGavel,
  TbHeartHandshake,
  TbId,
  TbLogin,
  TbMessages,
  TbParking,
  TbPhone,
  TbPhoneCall,
  TbPlayerPause,
  TbReceipt,
  TbRefresh,
  TbScale,
  TbSettings,
  TbSignature,
  TbTargetArrow,
  TbUserPlus,
} from "react-icons/tb";
import { LifeCycle, Resource } from "../../dtos/stage.dto";

export enum StageIconTag {
  FILE = "file",
  FILE_DESCRIPTION = "file_description",
  LOANS = "loans",
  BANK = "bank",
  LAWSUIT = "lawsuit",
  GAVEL = "gavel",
  STRATEGY = "strategy",
  PERSONAL_INFO = "personal_info",
  AGREEMENT = "agreement",
  SIGNED = "signed",
  WELCOME_CALL = "welcome_call",
  PHONE = "phone",
  PAYMENT = "payment",
  CREDIT_CARD = "credit_card",
  MONEY = "money",
  NEGOTIATION = "negotiation",
  HANDSHAKE = "handshake",
  CHECK = "check",
  SUCCESS = "success",
  WARNING = "warning",
  ALERT = "alert",
  PAUSE = "pause",
  CANCEL = "cancel",
  REFRESH = "refresh",
  SETTINGS = "settings",
  PARKING = "parking",
  ONBOARDING = "onboarding",
}

export enum StageColor {
  PINK = "pink",
  PURPLE = "purple",
  MAGENTA = "magenta",
  BLUE = "blue",
  CYAN = "cyan",
  INDIGO = "indigo",
  ORANGE = "orange",
  GREEN = "green",
  TEAL = "teal",
  RED = "red",
  YELLOW = "yellow",
  LIME = "lime",
  VIOLET = "violet",
  ROSE = "rose",
  AMBER = "amber",
  SLATE = "slate",
}

export const STAGE_ICONS: Record<StageIconTag, IconType> = {
  [StageIconTag.FILE]: TbFile,
  [StageIconTag.FILE_DESCRIPTION]: TbFileDescription,
  [StageIconTag.LOANS]: TbCashBanknote,
  [StageIconTag.BANK]: TbBuildingBank,
  [StageIconTag.LAWSUIT]: TbScale,
  [StageIconTag.GAVEL]: TbGavel,
  [StageIconTag.STRATEGY]: TbTargetArrow,
  [StageIconTag.PERSONAL_INFO]: TbId,
  [StageIconTag.AGREEMENT]: TbFileCertificate,
  [StageIconTag.SIGNED]: TbSignature,
  [StageIconTag.WELCOME_CALL]: TbPhoneCall,
  [StageIconTag.PHONE]: TbPhone,
  [StageIconTag.PAYMENT]: TbReceipt,
  [StageIconTag.CREDIT_CARD]: TbCreditCard,
  [StageIconTag.MONEY]: TbCoin,
  [StageIconTag.NEGOTIATION]: TbMessages,
  [StageIconTag.HANDSHAKE]: TbHeartHandshake,
  [StageIconTag.CHECK]: TbCheck,
  [StageIconTag.SUCCESS]: TbCircleCheck,
  [StageIconTag.WARNING]: TbAlertTriangle,
  [StageIconTag.ALERT]: TbAlertCircle,
  [StageIconTag.PAUSE]: TbPlayerPause,
  [StageIconTag.CANCEL]: TbCircleX,
  [StageIconTag.REFRESH]: TbRefresh,
  [StageIconTag.SETTINGS]: TbSettings,
  [StageIconTag.PARKING]: TbParking,
  [StageIconTag.ONBOARDING]: TbUserPlus,
};

export const FALLBACK_STAGE_ICON = TbCircleDashed;

export const resolveStageIcon = (icon?: string): IconType => STAGE_ICONS[icon as StageIconTag] ?? FALLBACK_STAGE_ICON;

const titleise = (input: string): string =>
  input
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export const toStageValue = (input: string): string =>
  input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 48);

export const STAGE_ICON_LIST: Array<{
  value: StageIconTag;
  label: string;
  Icon: IconType;
}> = Object.values(StageIconTag).map((value) => ({
  value,
  label: titleise(value),
  Icon: STAGE_ICONS[value],
}));

export interface StageColorToken {
  label: string;
  solid: string;
  bg: string;
  border: string;
  text: string;
}

export const STAGE_COLORS: Record<StageColor, StageColorToken> = {
  [StageColor.PINK]: {
    label: "Pink",
    solid: "#f472b6",
    bg: "#fdf2f8",
    border: "#f9a8d4",
    text: "#be185d",
  },
  [StageColor.PURPLE]: {
    label: "Purple",
    solid: "#722ed1",
    bg: "#f9f0ff",
    border: "#d3adf7",
    text: "#531dab",
  },
  [StageColor.MAGENTA]: {
    label: "Magenta",
    solid: "#eb2f96",
    bg: "#fff0f6",
    border: "#ffadd2",
    text: "#c41d7f",
  },
  [StageColor.BLUE]: {
    label: "Blue",
    solid: "#1677ff",
    bg: "#e6f4ff",
    border: "#91caff",
    text: "#0958d9",
  },
  [StageColor.CYAN]: {
    label: "Cyan",
    solid: "#13c2c2",
    bg: "#e6fffb",
    border: "#87e8de",
    text: "#08979c",
  },
  [StageColor.INDIGO]: {
    label: "Indigo",
    solid: "#2f54eb",
    bg: "#f0f5ff",
    border: "#adc6ff",
    text: "#1d39c4",
  },
  [StageColor.ORANGE]: {
    label: "Orange",
    solid: "#fa8c16",
    bg: "#fff7e6",
    border: "#ffd591",
    text: "#d46b08",
  },
  [StageColor.GREEN]: {
    label: "Green",
    solid: "#52c41a",
    bg: "#f6ffed",
    border: "#b7eb8f",
    text: "#389e0d",
  },
  [StageColor.TEAL]: {
    label: "Teal",
    solid: "#14b8a6",
    bg: "#f0fdfa",
    border: "#5eead4",
    text: "#0f766e",
  },
  [StageColor.RED]: {
    label: "Red",
    solid: "#f5222d",
    bg: "#fff1f0",
    border: "#ffa39e",
    text: "#cf1322",
  },
  [StageColor.YELLOW]: {
    label: "Yellow",
    solid: "#fadb14",
    bg: "#feffe6",
    border: "#fffb8f",
    text: "#ad8b00",
  },
  [StageColor.LIME]: {
    label: "Lime",
    solid: "#a0d911",
    bg: "#fcffe6",
    border: "#eaff8f",
    text: "#5b8c00",
  },
  [StageColor.VIOLET]: {
    label: "Violet",
    solid: "#8b5cf6",
    bg: "#f5f3ff",
    border: "#c4b5fd",
    text: "#6d28d9",
  },
  [StageColor.ROSE]: {
    label: "Rose",
    solid: "#f43f5e",
    bg: "#fff1f2",
    border: "#fda4af",
    text: "#be123c",
  },
  [StageColor.AMBER]: {
    label: "Amber",
    solid: "#f59e0b",
    bg: "#fffbeb",
    border: "#fcd34d",
    text: "#b45309",
  },
  [StageColor.SLATE]: {
    label: "Slate",
    solid: "#64748b",
    bg: "#f8fafc",
    border: "#cbd5e1",
    text: "#475569",
  },
};

export const FALLBACK_STAGE_COLOR: StageColorToken = STAGE_COLORS[StageColor.SLATE];

export const resolveStageColor = (color?: string): StageColorToken =>
  STAGE_COLORS[color as StageColor] ?? FALLBACK_STAGE_COLOR;

export const STAGE_COLOR_LIST: Array<{
  value: StageColor;
  token: StageColorToken;
}> = Object.values(StageColor).map((value) => ({
  value,
  token: STAGE_COLORS[value],
}));

export interface LifeCycleMeta {
  label: string;
  description: string;
  Icon: IconType;
  color: StageColorToken;
}

export const LIFECYCLE_META: Record<LifeCycle, LifeCycleMeta> = {
  [LifeCycle.ENTRY]: {
    label: "Entry",
    description: "Where a record lands when it is first created",
    Icon: TbLogin,
    color: STAGE_COLORS[StageColor.GREEN],
  },
  [LifeCycle.INTERMEDIATE]: {
    label: "Intermediate",
    description: "Work in progress, automations may move records through",
    Icon: TbArrowNarrowRight,
    color: STAGE_COLORS[StageColor.BLUE],
  },
  [LifeCycle.STALLED]: {
    label: "Stalled",
    description: "Parked until someone moves it by hand",
    Icon: TbPlayerPause,
    color: STAGE_COLORS[StageColor.AMBER],
  },
  [LifeCycle.EXIT]: {
    label: "Exit",
    description: "Closed out, nothing moves after this",
    Icon: TbFlagCheck,
    color: STAGE_COLORS[StageColor.SLATE],
  },
};

export const LIFECYCLE_ORDER: LifeCycle[] = [
  LifeCycle.ENTRY,
  LifeCycle.INTERMEDIATE,
  LifeCycle.STALLED,
  LifeCycle.EXIT,
];

export const RESOURCE_LABELS: Record<Resource, string> = {
  [Resource.APPLICATION]: "Applications",
  [Resource.LOAN]: "Loans",
};

export const RESOURCE_OPTIONS = Object.values(Resource).map((value) => ({
  value,
  label: RESOURCE_LABELS[value] ?? titleise(value),
}));
